import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import environmentConfig from '../config/environment.js';
import { syncPendingNotes } from '../utils/noteSyncUtil.js';
import { useSnackbar } from '../components/Material3';
import { useNavigate } from 'react-router-dom';

/**
 * Storage keys (single source of truth) - Using centralized config
 * NOTE: Tokens are now stored in HttpOnly cookies for security.
 * Only user data is kept in localStorage for quick access.
 */
const USER_KEY = 'shelfquest_user';
const TOKEN_KEY = environmentConfig.getTokenKey?.() || 'shelfquest_token';
const REFRESH_TOKEN_KEY = 'shelfquest_refresh_token';

/**
 * Mutex to prevent concurrent refresh attempts
 * This is critical for avoiding token family breach detection false positives
 * When multiple requests fail simultaneously, they'll all wait for the same refresh
 */
let refreshPromise = null;

/**
 * Use centralized environment configuration for API URL
 * This ensures consistency across the application and proper environment handling
 */
const API_URL = environmentConfig.apiUrl;

/**
 * Context
 */
const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  // State
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // gate initial render
  const [error, setError] = useState(null);

  // Ref: when true, the verify effect skips re-verification because the
  // session was just established by a fresh login/register/refresh response.
  const freshAuthRef = useRef(false);

  const persistFallbackTokens = useCallback((payload) => {
    try {
      if (payload?.token) {
        localStorage.setItem(TOKEN_KEY, payload.token);
      }
      if (payload?.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, payload.refreshToken);
      }
    } catch {
      // Ignore storage failures (private mode/quota)
    }
  }, []);

  const clearFallbackTokens = useCallback(() => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      // Ignore storage failures
    }
  }, []);

  // Note: We no longer store token in state since it's in HttpOnly cookies
  // The browser automatically includes it in requests via credentials: 'include'

  // Debug (dev only)
  if (import.meta.env.DEV) {
     
    console.warn('🔗 AuthContext API_URL:', API_URL);
  }

  /**
   * Core fetch wrapper.
   * Now uses HttpOnly cookies for authentication.
   * No need to manually attach Authorization header - cookies are sent automatically.
   * Throws on non-2xx with parsed message where possible.
   */
  const makeApiCall = useCallback(
    async (endpoint, options = {}) => {
      const url = `${API_URL}${endpoint}`;
      // Include Authorization header when a token exists (fallback),
      // but rely on HttpOnly cookies as the primary auth mechanism
      const baseHeaders = environmentConfig.getAuthHeaders();
      const headers = {
        'Content-Type': 'application/json',
        ...baseHeaders,
        ...(options.headers || {}),
      };
      // Fallback: use header token when cookie transport is blocked.
      try {
        const fallbackToken = localStorage.getItem(TOKEN_KEY);
        if (fallbackToken && !headers.Authorization) {
          headers.Authorization = `Bearer ${fallbackToken}`;
        }
      } catch {
        // Ignore storage failures
      }

      const config = {
        method: options.method || 'GET',
        headers,
        body: options.body,
        credentials: 'include', // CRITICAL: Send cookies with every request
        signal: options.signal,
      };

      let res;
      try {
        res = await fetch(url, config);
      } catch {
        throw new Error('Network error. Please check your connection.');
      }

      let data = null;
      const isJson = res.headers.get('content-type')?.includes('application/json');
      if (isJson) {
        try {
          data = await res.json();
        } catch {
          // ignore parse error, fallback below
        }
      } else {
        // for non-JSON responses, try text
        try {
          data = await res.text();
        } catch {
          // ignore
        }
      }

      if (!res.ok) {
        const message =
          (data && (data.error || data.message)) ||
          `Request failed (${res.status})`;
        // Preserve the HTTP status and server error code on the Error so callers
        // (e.g. the login page) can distinguish a 429 rate-limit / lockout from a
        // 401 bad-credentials without string-matching the message.
        const error = new Error(message);
        error.status = res.status;
        if (data && data.code) error.code = data.code;
        throw error;
      }

      return data;
    },
    [] // No dependencies - cookies handled automatically
  );

  /**
   * Attempt to refresh the token using the refresh endpoint
   * Uses HttpOnly cookies - refresh token is automatically sent by browser
   *
   * CRITICAL: Uses mutex pattern to prevent concurrent refresh attempts
   * This prevents token family breach detection false positives
   */
  const attemptTokenRefresh = useCallback(async () => {
    // If refresh is already in progress, wait for it
    if (refreshPromise) {
      console.warn('🔄 [AUTH] Refresh already in progress, waiting for existing refresh...');
      return await refreshPromise;
    }

    // Create new refresh promise with mutex protection
    refreshPromise = (async () => {
      try {
        console.warn('🔄 [AUTH] Initiating token refresh via HttpOnly cookies...');
        const refreshStartTime = Date.now();

        let refreshTokenBody;
        try {
          const fallbackRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
          if (fallbackRefreshToken) {
            refreshTokenBody = JSON.stringify({ refreshToken: fallbackRefreshToken });
          }
        } catch {
          // Ignore storage failures
        }

        const response = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include', // Include HttpOnly cookies (refresh token)
          headers: {
            'Content-Type': 'application/json',
          },
          body: refreshTokenBody,
        });

        const refreshDuration = Date.now() - refreshStartTime;

        if (response.ok) {
          const data = await response.json();
          console.warn(`✅ [AUTH] Token refresh successful (${refreshDuration}ms) - new cookies set by server`);
          persistFallbackTokens(data);

          // Update user data if provided
          if (data.user) {
            freshAuthRef.current = true;
            setUser(data.user);
            localStorage.setItem(USER_KEY, JSON.stringify(data.user));
            console.warn('    ↳ User data updated in state and localStorage');
          }

          // Return true to indicate success
          return true;
        }

        console.warn(`❌ [AUTH] Token refresh failed (${refreshDuration}ms): ${response.status} ${response.statusText}`);
        return false;
      } catch (err) {
        console.error('❌ [AUTH] Token refresh error:', err.message);
        return false;
      } finally {
        // Clear the mutex after completion (success or failure)
        console.warn('🔓 [AUTH] Refresh mutex released');
        refreshPromise = null;
      }
    })();

    // Wait for and return the refresh promise
    return await refreshPromise;
  }, [persistFallbackTokens]);

  /**
   * Wrap protected calls to handle token expiry centrally.
   * Automatically refreshes tokens using HttpOnly cookies.
   */
  const makeAuthenticatedApiCall = useCallback(
    async (endpoint, options = {}) => {
      try {
        return await makeApiCall(endpoint, options);
      } catch (err) {
        const msg = String(err?.message || '');
        const expired =
          msg.includes('401') ||
          msg.includes('403') ||
          /jwt\s*expired/i.test(msg) ||
          /TokenExpiredError/i.test(msg) ||
          /Token verification failed/i.test(msg) ||
          /Access token required/i.test(msg) ||
          /NO_TOKEN/i.test(msg) ||
          /INVALID_TOKEN/i.test(msg);

        if (expired) {
          console.warn('🔄 Token expired/invalid → attempting refresh');

          // Try to refresh the token via cookies
          const refreshSuccess = await attemptTokenRefresh();

          if (refreshSuccess) {
            // Retry the original request - new cookies are automatically included
            console.warn('🔄 Retrying original request with refreshed cookies');
            return await makeApiCall(endpoint, options);
          } else {
            // Refresh failed, prompt re-login
            console.warn('❌ Token refresh failed → prompting re-login');
            try {
              showSnackbar({
                message: 'Session expired. Please sign in again.',
                variant: 'warning',
                duration: 5000,
                position: 'top-center',
                action: (
                  <button className="md3-snackbar__action-button" onClick={() => navigate('/login')}>
                    Sign in
                  </button>
                )
              });
            } catch {
              // Silently ignore snackbar errors
            }
            localStorage.removeItem(USER_KEY);
            setUser(null);
            clearFallbackTokens();
            throw new Error('Your session has expired. Please log in again.');
          }
        }
        throw err;
      }
    },
    [makeApiCall, attemptTokenRefresh, navigate, showSnackbar, clearFallbackTokens]
  );

  /**
   * Bootstrap: load existing user data from localStorage
   * Tokens are in HttpOnly cookies, so we only need to restore user info.
   * If cookies are present, the user is logged in; if not, first API call will fail.
   */
  useEffect(() => {
    try {
      // One-time migration: remove stale tokens left from localStorage-based auth
      localStorage.removeItem('token');

      const storedUser = localStorage.getItem(USER_KEY);

      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch {
          // corrupted storage → clear
          localStorage.removeItem(USER_KEY);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Listen for global refresh failures (from axios paths) and prompt re-login
  useEffect(() => {
    const handler = () => {
      try {
        showSnackbar({
          message: 'Session expired. Please sign in again.',
          variant: 'warning',
          duration: 5000,
          position: 'top-center',
          action: (
            <button className="md3-snackbar__action-button" onClick={() => navigate('/login')}>
              Sign in
            </button>
          )
        });
      } catch {
        // Silently ignore snackbar errors
      }
    };
    window.addEventListener('auth-refresh-failed', handler);
    return () => window.removeEventListener('auth-refresh-failed', handler);
  }, [showSnackbar, navigate]);

  /**
   * Optional explicit verification (call when you need it)
   * Uses simple makeApiCall to avoid triggering auto-refresh
   * This is a CHECK operation, not a refresh operation
   */
  const verifyToken = useCallback(
    async () => {
      console.warn('🔍 [AUTH] Verifying token validity...');
      const data = await makeApiCall('/auth/profile');
      console.warn('✅ [AUTH] Token verification successful');
      return data;
    },
    [makeApiCall]
  );

  // Verify cookie/header session after restoring user from storage.
  // Uses makeAuthenticatedApiCall so an expired access token is automatically
  // refreshed via the refresh token cookie (7-day lifetime) before giving up.
  // Skips verification when the user was just set by a fresh auth action
  // (login, register, or token refresh) to avoid a destructive re-verify loop.
  useEffect(() => {
    let cancelled = false;

    const verifyIfNeeded = async () => {
      if (!user) {
        console.warn('ℹ️ [AUTH] No user in state, skipping verification');
        setLoading(false);
        return;
      }

      // Fresh login/register/refresh — session is already verified by the API response
      if (freshAuthRef.current) {
        console.warn('ℹ️ [AUTH] Skipping verification — session just established');
        freshAuthRef.current = false;
        return;
      }

      console.warn('🔍 [AUTH] User found in localStorage, verifying session via cookies...');

      try {
        setLoading(true);
        // Use the authenticated wrapper so that an expired access token
        // triggers an automatic refresh attempt before we give up.
        await makeAuthenticatedApiCall('/auth/profile');
        console.warn('✅ [AUTH] Session verification complete');
      } catch (err) {
        if (!cancelled) {
          console.warn('❌ [AUTH] Session verification failed:', err.message);
          // makeAuthenticatedApiCall already clears user state on auth failures
          // (expired refresh token, revoked session, etc.).
          // For transient network errors, keep user state — they may be offline.
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    verifyIfNeeded();
    return () => { cancelled = true; };
  }, [user, makeAuthenticatedApiCall]);

  /**
   * Auth actions
   */
  const register = useCallback(
    async (email, password, name) => {
      setLoading(true);
      setError(null);
      try {
        const data = await makeApiCall('/auth/secure/register', {
          method: 'POST',
          body: JSON.stringify({ email, password, name }),
        });

        // Store user data only — tokens are in httpOnly cookies
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        persistFallbackTokens(data);
        freshAuthRef.current = true;
        setUser(data.user);

        console.warn('✅ Registration successful - using HttpOnly cookie authentication');
        return { success: true, user: data.user };
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    [makeApiCall, persistFallbackTokens]
  );

  const login = useCallback(
    async (email, password) => {
      setLoading(true);
      setError(null);
      try {
        const data = await makeApiCall('/auth/secure/login', {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        });

        // Store user data only — tokens are in httpOnly cookies
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        persistFallbackTokens(data);
        freshAuthRef.current = true;
        setUser(data.user);

        // Track daily login for gamification (once per day)
        try {
          const lastLogin = localStorage.getItem('lastDailyLogin');
          const today = new Date().toDateString();

          if (lastLogin !== today) {
            // Mark as logged in today
            localStorage.setItem('lastDailyLogin', today);

            // Dispatch event for gamification context to pick up
            window.dispatchEvent(new CustomEvent('dailyLoginTracked', {
              detail: {
                userId: data.user.id,
                timestamp: new Date().toISOString(),
                date: today
              }
            }));

            console.warn('✅ Daily login tracked - 10 points will be awarded');
          } else {
            console.warn('ℹ️ Already logged in today - no additional points');
          }
        } catch (loginTrackError) {
          console.warn('Failed to track daily login:', loginTrackError);
          // Don't fail login if tracking fails
        }

        // Sync any pending notes that were saved locally while offline/logged out
        try {
          const syncResults = await syncPendingNotes();

          if (syncResults.synced > 0) {
            console.warn(`✅ Synced ${syncResults.synced} pending notes after login`);

            // Dispatch event so UI can show notification
            window.dispatchEvent(new CustomEvent('pendingNotesSynced', {
              detail: {
                synced: syncResults.synced,
                failed: syncResults.failed,
                timestamp: new Date().toISOString()
              }
            }));
          }

          if (syncResults.failed > 0) {
            console.warn(`⚠️ Failed to sync ${syncResults.failed} notes - will retry later`);
          }
        } catch (syncError) {
          console.warn('Failed to sync pending notes:', syncError);
          // Don't fail login if sync fails
        }

        console.warn('✅ Login successful - using HttpOnly cookie authentication');
        return { success: true, user: data.user };
      } catch (err) {
        setError(err.message);
        // Surface status/code so the UI can tell rate-limit/lockout (429) apart
        // from invalid credentials (401).
        return { success: false, error: err.message, status: err.status, code: err.code };
      } finally {
        setLoading(false);
      }
    },
    [makeApiCall, persistFallbackTokens]
  );

  const loginWithGoogle = useCallback(
    async (credential) => {
      setLoading(true);
      setError(null);
      try {
        const data = await makeApiCall('/auth/secure/google', {
          method: 'POST',
          body: JSON.stringify({ credential }),
        });

        // Store user data only — tokens are in httpOnly cookies
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        persistFallbackTokens(data);
        freshAuthRef.current = true;
        setUser(data.user);

        // Track daily login for gamification (once per day)
        try {
          const lastLogin = localStorage.getItem('lastDailyLogin');
          const today = new Date().toDateString();

          if (lastLogin !== today) {
            localStorage.setItem('lastDailyLogin', today);

            window.dispatchEvent(new CustomEvent('dailyLoginTracked', {
              detail: {
                userId: data.user.id,
                timestamp: new Date().toISOString(),
                date: today
              }
            }));
          }
        } catch (loginTrackError) {
          console.warn('Failed to track daily login:', loginTrackError);
        }

        // Sync any pending notes
        try {
          const syncResults = await syncPendingNotes();

          if (syncResults.synced > 0) {
            window.dispatchEvent(new CustomEvent('pendingNotesSynced', {
              detail: {
                synced: syncResults.synced,
                failed: syncResults.failed,
                timestamp: new Date().toISOString()
              }
            }));
          }
        } catch (syncError) {
          console.warn('Failed to sync pending notes:', syncError);
        }

        console.warn('✅ Google login successful - using HttpOnly cookie authentication');
        return {
          success: true,
          user: data.user,
          isNewUser: data.isNewUser,
          isLinkedAccount: data.isLinkedAccount
        };
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    [makeApiCall, persistFallbackTokens]
  );

  const logout = useCallback(async () => {
    try {
      // Call backend to clear HttpOnly cookies
      await makeApiCall('/auth/logout', {
        method: 'POST',
      });
      console.warn('✅ Logout successful - cookies cleared on server');
    } catch (err) {
      console.warn('⚠️ Logout API call failed, clearing local state anyway:', err);
    } finally {
      // Always clear local state, even if API call fails
      localStorage.removeItem(USER_KEY);
      clearFallbackTokens();
      setUser(null);
      setError(null);
    }
  }, [makeApiCall, clearFallbackTokens]);

  const updateProfile = useCallback(
    async (updates) => {
      setError(null);
      try {
        const data = await makeAuthenticatedApiCall('/auth/profile', {
          method: 'PUT',
          body: JSON.stringify(updates),
        });
        const updatedUser = { ...user, ...data };
        setUser(updatedUser);
        localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
        return { success: true, user: updatedUser };
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      }
    },
    [makeAuthenticatedApiCall, user]
  );

  const changePassword = useCallback(
    async (currentPassword, newPassword) => {
      setError(null);
      try {
        await makeAuthenticatedApiCall('/auth/secure/change-password', {
          method: 'POST',
          body: JSON.stringify({ currentPassword, newPassword }),
        });
        return { success: true };
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      }
    },
    [makeAuthenticatedApiCall]
  );

  const requestPasswordReset = useCallback(
    async (email) => {
      setError(null);
      try {
        await makeApiCall('/auth/secure/reset-password', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
        return { success: true };
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      }
    },
    [makeApiCall]
  );

  const deleteAccount = useCallback(
    async (password) => {
      setError(null);
      try {
        await makeAuthenticatedApiCall('/auth/delete-account', {
          method: 'DELETE',
          body: JSON.stringify({ password }),
        });
        // Clear local state/storage after successful deletion
        logout();
        return { success: true };
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      }
    },
    [makeAuthenticatedApiCall, logout]
  );

  const refreshUser = useCallback(
    async () => {
      try {
        const data = await verifyToken();
        const updated = { ...(user || {}), ...data };
        setUser(updated);
        localStorage.setItem(USER_KEY, JSON.stringify(updated));
        return { success: true, user: updated };
      } catch (err) {
        // On failure, log out to clear bad state
        await logout();
        return { success: false, error: err.message };
      }
    },
    [user, verifyToken, logout]
  );

  const hasRole = useCallback((role) => Boolean(user?.roles?.includes(role)), [user]);
  const clearError = useCallback(() => setError(null), []);

  // User is authenticated if we have user data (cookies handle the actual auth)
  const isAuthenticated = useMemo(() => Boolean(user), [user]);

  // Expose fallback token for compatibility in cookie-restricted environments.
  const token = useMemo(() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  }, [user]);

  const value = useMemo(
    () => ({
      // state
      user,
      loading,
      error,
      isAuthenticated,
      token,

      // actions
      register,
      login,
      loginWithGoogle,
      logout,
      updateProfile,
      changePassword,
      requestPasswordReset,
      deleteAccount,
      refreshUser,
      clearError,
      hasRole,

      // utilities
      makeApiCall,
      makeAuthenticatedApiCall,
    }),
    [
      user,
      loading,
      error,
      isAuthenticated,
      token,
      register,
      login,
      loginWithGoogle,
      logout,
      updateProfile,
      changePassword,
      requestPasswordReset,
      deleteAccount,
      refreshUser,
      clearError,
      hasRole,
      makeApiCall,
      makeAuthenticatedApiCall,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Optional HOC for protecting pages that don’t use router guards.
 */
export const withAuth = (Component) => function WithAuth(props) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', flexDirection: 'column', gap: 16
      }}>
        <div style={{
          width: 40, height: 40, border: '3px solid #e0e0e0',
          borderTop: '3px solid var(--md-sys-color-primary)', borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: 'var(--md-sys-color-primary)' }}>Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', flexDirection: 'column', gap: 16, padding: 24
      }}>
        <h2>Authentication Required</h2>
        <p>Please log in to access this page.</p>
      </div>
    );
  }

  return <Component {...props} />;
};

export default AuthContext;
